/**
 * Favorite foods — lb_users/{uid}/favorite_foods, exact port of the mobile
 * services/firebase/favorites.ts shapes: nutrients stored PER SINGLE SERVING
 * (entry totals divided back by servingsConsumed).
 */
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { scaleNutrients, sumNutrients } from '@/lib/nutrients'
import { keepScoringBasis } from '@/log/rescore'
import type {
  DiaryEntry,
  DiaryEntrySource,
  FluidType,
  MealTime,
  Nutrients,
  SafetyLevel,
  StoredAnalysis,
  StoredFoodItem,
} from '@/types'

/** Canonical meal order — mobile services/firebase/favorites.ts MEAL_TIME_ORDER. */
export const MEAL_TIME_ORDER: MealTime[] = ['breakfast', 'lunch', 'dinner', 'snack']

export interface FavoriteFood {
  id: string
  name: string
  nutrients: Nutrients
  servingSize: number
  servingUnit: string
  safetyLevel: SafetyLevel
  photoUrl?: string
  source: DiaryEntrySource
  sourceId?: string
  foods?: StoredFoodItem[]
  analysis?: StoredAnalysis
  notes?: string
  // Fluid content, stored PER 1 SERVING (mirrors mobile types/favorite.ts).
  fluidMl?: number
  fluidType?: FluidType
  // Meal times this food is usually eaten at. Seeded from the source diary
  // entry's mealType and unioned with each meal type it's later logged to.
  // Drives the "group by meal" view; absent = "Other" section (mobile parity —
  // an explicit empty array means the user cleared the tags).
  mealTimes?: MealTime[]
  createdAt?: Date
}

function favoritesRef(uid: string) {
  return collection(db, 'lb_users', uid, 'favorite_foods')
}

export function subscribeFavorites(
  uid: string,
  onUpdate: (foods: FavoriteFood[]) => void,
): () => void {
  const q = query(favoritesRef(uid), orderBy('createdAt', 'desc'))
  return onSnapshot(q, (snap) =>
    onUpdate(
      snap.docs.map((d) => {
        const data = d.data()
        return {
          ...(data as Omit<FavoriteFood, 'id' | 'createdAt'>),
          id: d.id,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : undefined,
        }
      }),
    ),
  )
}

/** Save a diary entry as a favorite — divides nutrients back to per-serving (mobile addFavorite). */
export async function addFavoriteFromEntry(uid: string, entry: DiaryEntry): Promise<string> {
  const servings = entry.servingsConsumed || 1
  // Store per single serving (total / servingsConsumed). scaleNutrients carries
  // EVERY optional nutrient the entry has — never drop fiber/satFat/cholesterol/etc.
  // 1.5.0: …and the raw scoring basis is kept, so a small serving's rounding
  // can't stop the saved food's score from following later edits (mobile addFavorite).
  const { nutrients: perServing } = keepScoringBasis(entry, { nutrients: scaleNutrients(entry.nutrients, 1 / servings) })
  const input = {
    name: entry.name,
    nutrients: perServing,
    servingSize: entry.servingSize || 1,
    servingUnit: entry.servingUnit || 'serving',
    safetyLevel: entry.safetyLevel || 'safe',
    ...(entry.photoUrl && { photoUrl: entry.photoUrl }),
    source: entry.source || 'manual',
    ...(entry.sourceId && { sourceId: entry.sourceId }),
    ...(entry.foods?.length && { foods: entry.foods }),
    ...(entry.analysis && Object.keys(entry.analysis).length > 0 && { analysis: entry.analysis }),
    ...(entry.notes && { notes: entry.notes }),
    // Fluid carries over per 1 serving (entry.fluidMl is for servingsConsumed
    // servings). Omitted when the source has no fluid.
    ...(entry.fluidMl != null && entry.fluidMl > 0 && {
      fluidMl: Math.round(entry.fluidMl / servings),
      fluidType: entry.fluidType || 'other',
    }),
    // Seed the meal-time tag from the entry being favorited (mobile addFavorite).
    ...(entry.mealType && { mealTimes: [entry.mealType] }),
  }
  const ref = await addDoc(favoritesRef(uid), { ...input, createdAt: serverTimestamp() })
  return ref.id
}

/**
 * Build a consistent combined name from food item names — mobile
 * buildFavoriteMealName. Format: "Item1 & Item2" or "Item1, Item2 & Item3".
 */
export function buildFavoriteMealName(names: string[]): string {
  return names.length <= 2
    ? names.join(' & ')
    : `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`
}

/**
 * Save a quick-log meal (its foods) as ONE favorite — port of mobile
 * addFavoriteMeal: nutrients + fluid summed across the items (servingSize 1
 * "meal", so the sum IS the per-serving value), the WORST item safety level,
 * source 'quick_log', no sourceId / photo / notes / meal-time tag.
 */
export async function addFavoriteMeal(
  uid: string,
  items: StoredFoodItem[],
  analysis?: StoredAnalysis,
  customName?: string,
): Promise<string> {
  const name = customName || buildFavoriteMealName(items.map((i) => i.name))
  const nutrients = sumNutrients(items.map((i) => i.nutrients))
  const levels: SafetyLevel[] = ['safe', 'moderate', 'limit']
  const safetyLevel = items.reduce<SafetyLevel>((worst, item) => {
    const level = item.safetyLevel || 'safe'
    return levels.indexOf(level) > levels.indexOf(worst) ? level : worst
  }, 'safe')
  const totalFluidMl = items.reduce((sum, item) => sum + (item.fluidMl || 0), 0)

  const ref = await addDoc(favoritesRef(uid), {
    name,
    nutrients,
    servingSize: 1,
    servingUnit: 'meal',
    safetyLevel,
    source: 'quick_log',
    foods: items,
    ...(analysis && Object.keys(analysis).length > 0 && { analysis }),
    ...(totalFluidMl > 0 && { fluidMl: Math.round(totalFluidMl), fluidType: 'other' }),
    createdAt: serverTimestamp(),
  })
  return ref.id
}

/**
 * Merge multiple favorites into a single new favorite (originals untouched) —
 * faithful port of mobile mergeFavorites. Concatenates each favorite's food
 * breakdown (synthesizing a single item for favorites that don't carry one),
 * sums per-serving nutrients + fluid, takes the worst safety level, unions
 * meal-time tags, and stores the result as a 1-serving "meal".
 */
export async function mergeFavorites(
  uid: string,
  favorites: FavoriteFood[],
  customName?: string,
): Promise<FavoriteFood> {
  const name = customName?.trim() || buildFavoriteMealName(favorites.map((f) => f.name))

  const foods: StoredFoodItem[] = favorites.flatMap((f) =>
    f.foods?.length
      ? f.foods
      : [{
          name: f.name,
          quantity: f.servingSize || 1,
          unit: f.servingUnit || 'serving',
          nutrients: f.nutrients,
          safetyLevel: f.safetyLevel || 'safe',
          ...((f.fluidMl || 0) > 0 && { isFluid: true, fluidMl: Math.round(f.fluidMl || 0) }),
        }],
  )

  // Each source favorite contributes 1 serving → the sum IS the merged
  // per-serving value. scaleNutrients(…, 1) rounds per the app convention.
  const nutrients = scaleNutrients(sumNutrients(favorites.map((f) => f.nutrients)), 1)

  const levels: SafetyLevel[] = ['safe', 'moderate', 'limit']
  const safetyLevel = favorites.reduce<SafetyLevel>((worst, f) => {
    const level = f.safetyLevel || 'safe'
    return levels.indexOf(level) > levels.indexOf(worst) ? level : worst
  }, 'safe')

  const totalFluidMl = favorites.reduce((sum, f) => sum + (f.fluidMl || 0), 0)
  const fluidTypes = new Set(
    favorites.filter((f) => (f.fluidMl || 0) > 0).map((f) => f.fluidType || 'other'),
  )
  const fluidType: FluidType = fluidTypes.size === 1 ? [...fluidTypes][0] : 'other'

  const photoUrl = favorites.find((f) => f.photoUrl)?.photoUrl

  // Union of the source favorites' meal-time tags, in canonical meal order.
  const mealTimeUnion = new Set(favorites.flatMap((f) => f.mealTimes || []))
  const mealTimes = MEAL_TIME_ORDER.filter((m) => mealTimeUnion.has(m))

  const input: Omit<FavoriteFood, 'id' | 'createdAt'> = {
    name,
    nutrients,
    servingSize: 1,
    servingUnit: 'meal',
    safetyLevel,
    source: 'manual',
    foods,
    ...(photoUrl && { photoUrl }),
    ...(totalFluidMl > 0 && { fluidMl: Math.round(totalFluidMl), fluidType }),
    ...(mealTimes.length > 0 && { mealTimes }),
  }

  const ref = await addDoc(favoritesRef(uid), { ...input, createdAt: serverTimestamp() })
  return { ...input, id: ref.id, createdAt: new Date() }
}

/**
 * One-time backfill: classify favorites saved before meal-time tags existed by
 * the meal types they were historically logged to — faithful port of mobile
 * backfillFavoriteMealTimes. Matches diary entries by favorite id (re-logs with
 * source 'favorite') and by trimmed/lowercased name, over the last 365 days.
 *
 * Only favorites with NO mealTimes field are touched — favorites the user
 * explicitly cleared (mealTimes: []) stay cleared. Returns the input list with
 * backfilled tags applied.
 */
export async function backfillFavoriteMealTimes(
  uid: string,
  favorites: FavoriteFood[],
): Promise<FavoriteFood[]> {
  const untagged = favorites.filter((f) => f.mealTimes === undefined)
  if (untagged.length === 0) return favorites

  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - 365)
  const cutoffStr = cutoff.toISOString().slice(0, 10)

  const entriesCol = collection(db, 'lb_diary_entries')
  let snap
  try {
    // Served by the existing (userId, date, timestamp) composite index.
    snap = await getDocs(query(entriesCol, where('userId', '==', uid), where('date', '>=', cutoffStr)))
  } catch (error) {
    // Range query needs a composite index; fall back to the single-field
    // userId query (always available) rather than skipping the backfill.
    console.warn('[favorites] ranged diary query failed, falling back to full scan', error)
    snap = await getDocs(query(entriesCol, where('userId', '==', uid)))
  }

  const normalizeName = (name: string) => name.trim().toLowerCase()

  const byName = new Map<string, Set<MealTime>>()
  const byFavoriteId = new Map<string, Set<MealTime>>()
  snap.docs.forEach((d) => {
    const data = d.data()
    const mealType = data.mealType as MealTime | undefined
    if (!mealType || !MEAL_TIME_ORDER.includes(mealType)) return
    if (typeof data.name === 'string' && data.name.trim()) {
      const key = normalizeName(data.name)
      const set = byName.get(key) || new Set<MealTime>()
      set.add(mealType)
      byName.set(key, set)
    }
    if (data.source === 'favorite' && typeof data.sourceId === 'string') {
      const set = byFavoriteId.get(data.sourceId) || new Set<MealTime>()
      set.add(mealType)
      byFavoriteId.set(data.sourceId, set)
    }
  })

  const batch = writeBatch(db)
  let updates = 0
  const result = favorites.map((f) => {
    if (f.mealTimes !== undefined) return f
    const tags = new Set<MealTime>([
      ...(byFavoriteId.get(f.id) || []),
      ...(byName.get(normalizeName(f.name)) || []),
    ])
    if (tags.size === 0) return f
    const mealTimes = MEAL_TIME_ORDER.filter((m) => tags.has(m))
    batch.update(doc(db, 'lb_users', uid, 'favorite_foods', f.id), { mealTimes })
    updates++
    return { ...f, mealTimes }
  })

  console.log(
    `[favorites] meal-time backfill: ${snap.size} diary entries scanned, ` +
      `${updates}/${untagged.length} untagged favorites classified`,
  )

  if (updates > 0) {
    await batch.commit()
  }
  return result
}

/**
 * Create a saved food directly (no diary entry) — the "New food" button on the
 * Saved Foods page. safetyLevel 'safe' matches the manual-entry convention.
 */
export async function createManualFavorite(
  uid: string,
  input: {
    name: string
    nutrients: Nutrients
    servingSize: number
    servingUnit: string
    fluidMl?: number
  },
): Promise<string> {
  const ref = await addDoc(favoritesRef(uid), {
    name: input.name,
    nutrients: input.nutrients,
    servingSize: input.servingSize,
    servingUnit: input.servingUnit,
    safetyLevel: 'safe',
    source: 'manual',
    ...(input.fluidMl != null && input.fluidMl > 0 && { fluidMl: input.fluidMl, fluidType: 'other' }),
    createdAt: serverTimestamp(),
  })
  return ref.id
}

/**
 * Edit a saved food (mobile services/firebase/favorites.ts updateFavorite parity).
 * Only these fields are ever written; undefined values are stripped before the
 * write so we never clobber a field with `undefined` (Firestore would reject it).
 */
export async function updateFavorite(
  uid: string,
  favoriteId: string,
  updates: Partial<
    Pick<
      FavoriteFood,
      | 'name'
      | 'nutrients'
      | 'servingSize'
      | 'servingUnit'
      | 'foods'
      | 'fluidMl'
      | 'fluidType'
      | 'mealTimes'
      | 'safetyLevel'
      | 'photoUrl'
      | 'source'
      | 'sourceId'
      | 'analysis'
      | 'notes'
    >
  >,
): Promise<void> {
  const clean: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(updates)) {
    if (value !== undefined) clean[key] = value
  }
  if (Object.keys(clean).length === 0) return
  await updateDoc(doc(db, 'lb_users', uid, 'favorite_foods', favoriteId), clean)
}

export async function removeFavorite(uid: string, favoriteId: string): Promise<void> {
  await deleteDoc(doc(db, 'lb_users', uid, 'favorite_foods', favoriteId))
}

/**
 * Remove the favorites saved FROM a meal (sourceId === mealId) — mobile
 * removeFavoriteBySourceId, used when the source meal is deleted. Never
 * matches by name: an unrelated saved food with the same name stays.
 */
export async function removeFavoriteBySourceId(uid: string, sourceId: string): Promise<void> {
  const snap = await getDocs(query(favoritesRef(uid), where('sourceId', '==', sourceId)))
  if (snap.empty) return
  const batch = writeBatch(db)
  snap.docs.forEach((d) => batch.delete(d.ref))
  await batch.commit()
}

/** Find an existing favorite matching an entry (by sourceId, else exact name). */
export async function findFavoriteId(
  uid: string,
  entry: { sourceId?: string; name: string },
): Promise<string | null> {
  const snap = await getDocs(favoritesRef(uid))
  for (const d of snap.docs) {
    const data = d.data()
    if (entry.sourceId && data.sourceId === entry.sourceId) return d.id
    if (data.name === entry.name) return d.id
  }
  return null
}
